/**
 * @file: This file holds the label menu of the Compose page.
 *
 * An operator finds a clip without a kind label while the recipe is on screen.
 * The clip then stands in the "No kind label" group, and the spread rule cannot
 * keep it away from its neighbours. The label is data of the library, so the
 * operator had to leave Compose, set the label there and open Compose again.
 * A right-click on a row now sets it from the page (D14).
 *
 * The shared menu in client/common/context-menu.js holds the DOM, the position,
 * the delegated listener and the closing rules. This file holds only the
 * entries: which media was clicked, and which labels it has.
 *
 * The labels of the media are read from the server on every right-click. The
 * catalogue of the page can be behind a change a colleague made, and it does
 * not hold a media at all when a row is older than the library. The menu then
 * shows the labels as they are.
 */

import { ContextMenu } from 'common/context-menu'
import { NEUTRAL_GREY } from 'common/colours'
import { orderLabels } from 'common/label-order'

/** The two lists of the page. Each one names the media of a row in its own attribute. */
const LINE_SELECTOR = '[data-line-key]'
const ROW_SELECTOR = '[data-row-id]'

class LabelMenu {
  constructor (controller) {
    this.controller = controller
    this.menu = new ContextMenu({
      host: () => this._host(),
      // The listener runs outside Angular. Without this call the page does not
      // repaint after a label is set.
      onAfterRun: () => this.controller._$scope.$applyAsync()
    })
  }

  /** Listen on the component element. Call this once the template is linked. */
  attach () {
    this.menu.attach(this._host(), target => this.itemsAt(target))
  }

  /** Give up the listener and the element. Called when the page goes away. */
  detach () {
    this.menu.detach()
  }

  /**
   * The entries for the element under the cursor, as a promise. Null keeps the
   * menu of the browser, which the rest of the page needs.
   *
   * @param {Element} target - The element the operator clicked
   * @returns {Promise|null} A promise of the entries, or null
   */
  itemsAt (target) {
    const media = this.mediaAt(target)

    if (!media) {
      return null
    }

    return this.controller.labelWriter.labelsOf(media)
      .then(labels => this.itemsFor(media, labels))
      // The read is refused, so the menu shows what the page already knows.
      .catch(() => this.itemsFor(media, media.labels))
  }

  /**
   * The media of the row that holds an element. Everything else of the page,
   * such as the strip and the panel head, keeps the menu of the browser.
   *
   * @param {Element} target - The element the operator clicked
   * @returns {object|null} The media of that row, or null
   */
  mediaAt (target) {
    const line = target.closest(LINE_SELECTOR)

    if (line) {
      return this.controller.state.mediaOfLine(line.getAttribute('data-line-key'))
    }

    const row = target.closest(ROW_SELECTOR)

    return row ? this.controller.state.mediaOfRow(row.getAttribute('data-row-id')) : null
  }

  /**
   * The entries for one media: every label of the app as a check item, on when
   * the media has it.
   *
   * The kind labels come first. The kind is what the operator comes here for,
   * and the day labels and the scene labels are already set for most media.
   * Each group is sorted by name, as the chips of the page are.
   *
   * @param {object} media - The media of the row
   * @param {Array} [has] - The labels the media has now
   * @returns {Array} The entries for the shared menu
   */
  itemsFor (media, has) {
    const held = new Set((has || []).map(label => label.id))

    return orderLabels(this.controller.labelWriter.allLabels(), this.controller.state.sceneNames)
      .map(label => this._itemFor(media, label, held.has(label.id)))
  }

  // ===== Internals =====

  _itemFor (media, label, checked) {
    return {
      kind: 'check',
      label: label.name,
      checked,
      // A label with no colour of its own reads as the grey every other label
      // dot of the app reads.
      mark: { colour: label.color || NEUTRAL_GREY },
      run: () => this.controller.toggleMediaLabel(media, label, checked)
    }
  }

  /** The element of the component. Both lists and the menu are inside it. */
  _host () {
    return this.controller._$element && this.controller._$element[0]
  }
}

export { LabelMenu }
